/**
 * EmailBrandingTab Component
 * Manages email branding settings including colors, sender info, and templates
 */

import React, { useState } from 'react';
import {
  Mail,
  Palette,
  User,
  FileText,
  Eye,
  Code,
  RefreshCw,
  Save,
  CheckCircle,
  AlertCircle,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { ColorPicker } from '../../ui/ColorPicker';
import { OrganizationBranding } from '@/services/api/branding';

interface EmailBrandingTabProps {
  branding: Partial<OrganizationBranding>;
  onChange: (updates: Partial<OrganizationBranding>) => void;
  onSave: () => Promise<void>;
  onPreview: () => void;
  saving: boolean;
  organizationName: string;
}

export function EmailBrandingTab({
  branding,
  onChange,
  onSave,
  onPreview,
  saving,
  organizationName,
}: EmailBrandingTabProps) {
  const [showCustomCss, setShowCustomCss] = useState(false);

  const handleChange = (field: keyof OrganizationBranding, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* Sender Information */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <User className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sender Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Name
            </label>
            <input
              type="text"
              value={branding.email_from_name || ''}
              onChange={(e) => handleChange('email_from_name', e.target.value)}
              placeholder={organizationName}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Name that appears in recipients' inbox
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Email
            </label>
            <input
              type="email"
              value={branding.email_from_email || ''}
              onChange={(e) => handleChange('email_from_email', e.target.value)}
              placeholder="noreply@yourdomain.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Requires domain verification for deliverability
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reply-To Email
            </label>
            <input
              type="email"
              value={branding.email_reply_to || ''}
              onChange={(e) => handleChange('email_reply_to', e.target.value)}
              placeholder="support@yourdomain.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Where replies will be sent
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Domain
            </label>
            <input
              type="text"
              value={branding.email_domain || ''}
              onChange={(e) => handleChange('email_domain', e.target.value)}
              placeholder="yourdomain.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Domain Verification Status */}
        {branding.email_domain && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Domain Verification Status
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center">
                {branding.email_domain_verified ? (
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500 mr-2" />
                )}
                <span className="text-sm">Domain Verified</span>
              </div>
              <div className="flex items-center">
                {branding.email_dkim_configured ? (
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500 mr-2" />
                )}
                <span className="text-sm">DKIM Configured</span>
              </div>
              <div className="flex items-center">
                {branding.email_spf_configured ? (
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500 mr-2" />
                )}
                <span className="text-sm">SPF Configured</span>
              </div>
            </div>
            {!branding.email_domain_verified && (
              <p className="mt-3 text-xs text-gray-500">
                <Shield className="w-3 h-3 inline mr-1" />
                Contact support to configure domain authentication for improved deliverability.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Header Branding */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Mail className="w-5 h-5 mr-2 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Header</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Header Logo URL
            </label>
            <input
              type="text"
              value={branding.email_header_logo_url || ''}
              onChange={(e) => handleChange('email_header_logo_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Recommended: 200px wide, transparent background
            </p>
          </div>

          <ColorPicker
            label="Header Background Color"
            description="Background color for email header"
            value={branding.email_header_background_color || '#2563EB'}
            onChange={(color) => handleChange('email_header_background_color', color)}
          />
        </div>

        {/* Header Preview */}
        <div className="mt-4">
          <p className="text-xs text-gray-500 mb-2">Preview</p>
          <div
            className="p-4 rounded-lg text-center"
            style={{ backgroundColor: branding.email_header_background_color || '#2563EB' }}
          >
            {branding.email_header_logo_url ? (
              <img
                src={branding.email_header_logo_url}
                alt="Logo"
                className="max-h-12 mx-auto"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="text-white font-semibold">
                {branding.email_from_name || organizationName}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Email Colors */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Palette className="w-5 h-5 mr-2 text-pink-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Colors</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ColorPicker
            label="Primary Color"
            description="Buttons, links, and highlights"
            value={branding.email_primary_color || '#2563EB'}
            onChange={(color) => handleChange('email_primary_color', color)}
          />

          <ColorPicker
            label="Secondary Color"
            description="Secondary elements"
            value={branding.email_secondary_color || '#1E40AF'}
            onChange={(color) => handleChange('email_secondary_color', color)}
          />

          <ColorPicker
            label="Text Color"
            description="Main body text"
            value={branding.email_text_color || '#1F2937'}
            onChange={(color) => handleChange('email_text_color', color)}
          />

          <ColorPicker
            label="Background Color"
            description="Email body background"
            value={branding.email_background_color || '#FFFFFF'}
            onChange={(color) => handleChange('email_background_color', color)}
          />
        </div>
      </div>

      {/* Footer Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <FileText className="w-5 h-5 mr-2 text-green-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Footer</h3>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Footer HTML
            </label>
            <textarea
              value={branding.email_footer_html || ''}
              onChange={(e) => handleChange('email_footer_html', e.target.value)}
              rows={4}
              placeholder="<p>Your organization address</p><p>Contact: support@example.com</p>"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              HTML content for email footer. Include your address for CAN-SPAM compliance.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Footer Plain Text
            </label>
            <textarea
              value={branding.email_footer_text || ''}
              onChange={(e) => handleChange('email_footer_text', e.target.value)}
              rows={3}
              placeholder="Your Organization Name | 123 Main St, City, ST 12345"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Plain text version for email clients that don't support HTML.
            </p>
          </div>
        </div>
      </div>

      {/* Signature Template */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <User className="w-5 h-5 mr-2 text-orange-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Signature</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Signature Template
          </label>
          <textarea
            value={branding.email_signature_template || ''}
            onChange={(e) => handleChange('email_signature_template', e.target.value)}
            rows={4}
            placeholder="Best regards,&#10;{{sender_name}}&#10;{{sender_title}}&#10;{{organization_name}}"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Available variables: {'{{sender_name}}'}, {'{{sender_title}}'}, {'{{organization_name}}'}, {'{{sender_email}}'}, {'{{sender_phone}}'}
          </p>
        </div>
      </div>

      {/* Custom CSS (Advanced) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <button
          onClick={() => setShowCustomCss(!showCustomCss)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center">
            <Code className="w-5 h-5 mr-2 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Custom CSS (Advanced)
            </h3>
          </div>
          <span className="text-sm text-blue-600">
            {showCustomCss ? 'Hide' : 'Show'}
          </span>
        </button>

        {showCustomCss && (
          <div className="mt-4">
            <textarea
              value={branding.email_custom_css || ''}
              onChange={(e) => handleChange('email_custom_css', e.target.value)}
              rows={8}
              placeholder=".button { border-radius: 8px; }&#10;.header { padding: 20px; }"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-900 text-green-400 font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Advanced: Override default email template styles. Use with caution.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <button
          onClick={onPreview}
          className="flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Eye className="w-4 h-4 mr-2" />
          Preview Email
        </button>

        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

export default EmailBrandingTab;
